import React, { useRef, useState } from 'react';
import {
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
} from '@mui/material';
import { DateRangePicker } from './shared/DateRangePicker';
import { PopoverPortal, usePopoverPosition } from './shared/InlineEdit/popoverPortal';

interface DateRangeFilterProps {
  onFilterChange: (startDate: string | null, endDate: string | null) => void;
  currentStartDate?: string | null;
  currentEndDate?: string | null;
}

type PresetKey =
  | 'wtd' | 'mtd' | 'qtd' | 'ytd'
  | 'this-week' | 'this-month' | 'this-quarter' | 'this-year'
  | 'last-week' | 'last-month' | 'last-quarter' | 'last-year'
  | 'last-7' | 'last-30' | 'last-90' | 'last-120'
  | 'all-time';

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  onFilterChange,
  currentStartDate,
  currentEndDate,
}) => {
  const [mode, setMode] = useState<string>('between');
  const [startDate, setStartDate] = useState<string>(currentStartDate || '');
  const [endDate, setEndDate] = useState<string>(currentEndDate || '');
  // Design P3.2: the two native <input type="date"> for the "between" mode
  // are replaced by the canonical Beacon DateRangePicker in a popover
  // (same primitive BoardFilterPopover's "between" operator uses).
  const [rangeOpen, setRangeOpen] = useState(false);
  const rangeBtnRef = useRef<HTMLButtonElement>(null);
  const rangeCoords = usePopoverPosition(rangeBtnRef, rangeOpen, 360);

  const handleClear = () => {
    setStartDate('');
    setEndDate('');
    setMode('between');
    setRangeOpen(false);
    onFilterChange(null, null);
  };

  const rangeText =
    startDate || endDate ? `${startDate || '—'} → ${endDate || '—'}` : 'Pick range';

  const hasActiveFilter = currentStartDate || currentEndDate;

  const presets: Record<PresetKey, { label: string; range: { startDate: string; endDate: string } }> = {
    'wtd':          { label: 'Week to Date',    range: getWeekToDate() },
    'mtd':          { label: 'Month to Date',   range: getMonthToDate() },
    'qtd':          { label: 'Quarter to Date', range: getQuarterToDate() },
    'ytd':          { label: 'Year to Date',    range: getYearToDate() },
    'this-week':    { label: 'This Week (full)',    range: getThisWeek() },
    'this-month':   { label: 'This Month (full)',   range: getThisMonth() },
    'this-quarter': { label: 'This Quarter (full)', range: getThisQuarter() },
    'this-year':    { label: 'This Year (full)',    range: getThisYear() },
    'last-week':    { label: 'Last Week',    range: getLastWeek() },
    'last-month':   { label: 'Last Month',   range: getLastMonth() },
    'last-quarter': { label: 'Last Quarter', range: getLastQuarter() },
    'last-year':    { label: 'Last Year',    range: getLastYear() },
    'last-7':       { label: 'Last 7 Days',   range: getLastNDays(7) },
    'last-30':      { label: 'Last 30 Days',  range: getLastNDays(30) },
    'last-90':      { label: 'Last 90 Days',  range: getLastNDays(90) },
    'last-120':     { label: 'Last 120 Days', range: getLastNDays(120) },
    'all-time':     { label: 'All Time',       range: getAllTime() },
  };

  // Grouped menu structure
  const menuStructure: Array<{ header: string; keys: PresetKey[] }> = [
    { header: 'To Date',       keys: ['wtd', 'mtd', 'qtd', 'ytd'] },
    { header: 'Full Period',   keys: ['this-week', 'this-month', 'this-quarter', 'this-year'] },
    { header: 'Previous',      keys: ['last-week', 'last-month', 'last-quarter', 'last-year'] },
    { header: 'Rolling',       keys: ['last-7', 'last-30', 'last-90', 'last-120'] },
    { header: 'Other',         keys: ['all-time'] },
  ];

  const handleModeChange = (value: string) => {
    setMode(value);
    if (value === 'between') return;
    // Skip subheader values
    if (value.startsWith('__header')) return;
    const preset = presets[value as PresetKey];
    if (preset) {
      setStartDate(preset.range.startDate || '');
      setEndDate(preset.range.endDate || '');
      onFilterChange(preset.range.startDate, preset.range.endDate);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel sx={{ fontSize: '12.5px' }}>Date Filter</InputLabel>
        <Select
          label="Date Filter"
          value={mode}
          onChange={(e) => handleModeChange(e.target.value)}
          sx={{ height: 32, fontSize: '12.5px' }}
          MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
        >
          <MenuItem value="between">Between…</MenuItem>
          {menuStructure.map((group) => [
            <ListSubheader key={`__header_${group.header}`} sx={{ fontSize: '11px', lineHeight: '28px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', bgcolor: 'background.paper' }}>
              {group.header}
            </ListSubheader>,
            ...group.keys.map((key) => (
              <MenuItem key={key} value={key} sx={{ fontSize: '12.5px', pl: 3 }}>
                {presets[key].label}
              </MenuItem>
            )),
          ])}
        </Select>
      </FormControl>
      {mode === 'between' && (
        <>
          <button
            ref={rangeBtnRef}
            type="button"
            onClick={() => setRangeOpen((v) => !v)}
            style={{
              height: 32,
              display: 'inline-flex',
              alignItems: 'center',
              fontSize: '12.5px',
              fontFamily: 'inherit',
              padding: '0 12px',
              borderRadius: 4,
              border: '1px solid var(--rule)',
              background: 'var(--paper)',
              color: startDate || endDate ? 'var(--ink)' : 'var(--ink-faint)',
              cursor: 'pointer',
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
            }}
          >
            {rangeText}
          </button>
          <PopoverPortal
            open={rangeOpen}
            coords={rangeCoords}
            onClickOutside={() => setRangeOpen(false)}
            ignoreRefs={[rangeBtnRef]}
          >
            <DateRangePicker
              from={startDate || undefined}
              to={endDate || undefined}
              onSave={(next) => {
                setStartDate(next.from || '');
                setEndDate(next.to || '');
                onFilterChange(next.from || null, next.to || null);
                setRangeOpen(false);
              }}
              onCancel={() => setRangeOpen(false)}
            />
          </PopoverPortal>
        </>
      )}
      {hasActiveFilter && (
        <Button variant="text" size="small" onClick={handleClear} sx={{ fontSize: '12.5px', minWidth: 0, px: 0.5 }}>
          Clear
        </Button>
      )}
    </Box>
  );
};

export default DateRangeFilter;

function toYmd(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getLastNDays(days: number) {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - (days - 1));
  return { startDate: toYmd(start), endDate: toYmd(end) };
}

function getThisWeek() {
  const now = new Date();
  const day = now.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  const start = new Date(now);
  start.setDate(now.getDate() - (day === 0 ? 6 : day - 1)); // Monday
  const end = new Date(start);
  end.setDate(start.getDate() + 6); // Sunday
  return { startDate: toYmd(start), endDate: toYmd(end) };
}

function getLastWeek() {
  const now = new Date();
  const day = now.getDay();
  const thisMonday = new Date(now);
  thisMonday.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(thisMonday.getDate() - 7);
  const lastSunday = new Date(lastMonday);
  lastSunday.setDate(lastMonday.getDate() + 6);
  return { startDate: toYmd(lastMonday), endDate: toYmd(lastSunday) };
}

function getWeekToDate() {
  const now = new Date();
  const day = now.getDay();
  const start = new Date(now);
  start.setDate(now.getDate() - (day === 0 ? 6 : day - 1)); // Monday
  return { startDate: toYmd(start), endDate: toYmd(now) };
}

function getThisMonth() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { startDate: toYmd(start), endDate: toYmd(end) };
}

function getLastMonth() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  return { startDate: toYmd(start), endDate: toYmd(end) };
}

function getThisQuarter() {
  const now = new Date();
  const quarter = Math.floor(now.getMonth() / 3);
  const start = new Date(now.getFullYear(), quarter * 3, 1);
  const end = new Date(now.getFullYear(), quarter * 3 + 3, 0);
  return { startDate: toYmd(start), endDate: toYmd(end) };
}

function getLastQuarter() {
  const now = new Date();
  const quarter = Math.floor(now.getMonth() / 3);
  const start = new Date(now.getFullYear(), quarter * 3 - 3, 1);
  const end = new Date(now.getFullYear(), quarter * 3, 0);
  return { startDate: toYmd(start), endDate: toYmd(end) };
}

function getThisYear() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const end = new Date(now.getFullYear(), 11, 31);
  return { startDate: toYmd(start), endDate: toYmd(end) };
}

function getLastYear() {
  const now = new Date();
  const start = new Date(now.getFullYear() - 1, 0, 1);
  const end = new Date(now.getFullYear() - 1, 11, 31);
  return { startDate: toYmd(start), endDate: toYmd(end) };
}

function getYearToDate() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  return { startDate: toYmd(start), endDate: toYmd(now) };
}

function getQuarterToDate() {
  const now = new Date();
  const quarter = Math.floor(now.getMonth() / 3);
  const start = new Date(now.getFullYear(), quarter * 3, 1);
  return { startDate: toYmd(start), endDate: toYmd(now) };
}

function getMonthToDate() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return { startDate: toYmd(start), endDate: toYmd(now) };
}

function getAllTime() {
  const start = new Date(2000, 0, 1);
  const end = new Date();
  return { startDate: toYmd(start), endDate: toYmd(end) };
}
